import { TrashIcon } from '@phosphor-icons/react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { EditableList, EditableListItem } from '@/components/ui/editable-list'
import { popupFactory } from '@/factories'

import { MarketShopSettingsFormData } from '../market-shop-settings.types'

export const InventoryCategoriesSection = () => {
  const { t } = useTranslation(['market-shop-dashboard', 'common'])
  const { control, setValue } = useFormContext<MarketShopSettingsFormData>()
  const categories = useWatch({ control, name: 'categories' })

  const validate = (name: string, itemId?: string | number | null): string | undefined => {
    const isDuplicate = categories.some(
      (category) => category.name.toLowerCase() === name.toLowerCase() && category.id !== itemId,
    )
    const isAllCategory = name.toLowerCase() === 'all'
    if (name.length > 20) {
      return t('market-shop-dashboard:settings.categories.validation.maxLength')
    }
    if (isDuplicate) return t('market-shop-dashboard:settings.categories.validation.duplicate')
    if (isAllCategory) {
      return t('market-shop-dashboard:settings.categories.validation.reservedName')
    }
  }

  const handleAdd = (name: string) => {
    setValue(
      'categories',
      [...categories, { id: `new-category-${Date.now()}`, name, position: categories.length }],
      { shouldDirty: true, shouldTouch: true, shouldValidate: true },
    )
  }

  const handleRename = (item: EditableListItem, newName: string) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('market-shop-dashboard:settings.categories.rename.confirmBody', {
        newName,
        oldName: item.name,
      }),
      confirmText: t('market-shop-dashboard:settings.categories.rename.confirmButton'),
      onConfirm: async () => {
        setValue(
          'categories',
          categories.map((category) =>
            category.id === item.id ? { ...category, name: newName } : category,
          ),
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        )
      },
      title: t('market-shop-dashboard:settings.categories.rename.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleDelete = (item: EditableListItem) => {
    popupFactory.open(ConfirmationPopup, {
      children: t('market-shop-dashboard:settings.categories.delete.confirmBody', {
        name: item.name,
      }),
      confirmIcon: TrashIcon,
      confirmText: t('common:actions.delete'),
      onConfirm: () => {
        setValue(
          'categories',
          categories.filter((category) => category.id !== item.id),
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        )
      },
      title: t('market-shop-dashboard:settings.categories.delete.confirmTitle'),
      variant: 'danger',
    })
  }

  const handleReorder = (reorderedItems: EditableListItem[]) => {
    setValue(
      'categories',
      reorderedItems.flatMap((item, index) => {
        const category = categories.find((category) => category.id === item.id)
        return category ? [{ ...category, position: index }] : []
      }),
      { shouldDirty: true, shouldTouch: true, shouldValidate: true },
    )
  }

  const items: EditableListItem[] = [
    {
      id: 'all',
      name: t('market-shop-dashboard:settings.categories.allLabel'),
      pinned: true,
    },
    ...categories.map(({ id, name }) => ({ id, name })),
  ]

  return (
    <EditableList
      title={t('market-shop-dashboard:settings.categories.title')}
      description={t('market-shop-dashboard:settings.categories.description')}
      items={items}
      addLabel={t('market-shop-dashboard:settings.categories.addLabel')}
      maxLength={20}
      validate={validate}
      onAdd={handleAdd}
      onRename={handleRename}
      onDelete={handleDelete}
      onReorder={handleReorder}
    />
  )
}
